import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  output
} from "@angular/core";
import { FormBuilder, UntypedFormGroup, Validators, FormsModule } from "@angular/forms";
import { ModalController, IonButton, IonCheckbox, IonIcon, IonInput, IonItem, IonLabel, IonSelect, IonSelectOption } from "@ionic/angular";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { SmartComponentKind } from "src/components/shared-components/class/smart-components/smart-component-kind";
import { SmartComponentNewPropertySave } from "src/components/shared-components/class/smart-components/smart-component-new-property-save";
import { SmartComponentProperty } from "src/components/shared-components/class/smart-components/smart-component-property";
import { Environment } from "../../class/general-classes/environment";
import { SmartComponent } from "../../class/smart-components/smart-component";
import { SmartComponentEnvironmentAssociated } from "../../class/smart-components/smart-component-environment-associated";
import { SmartComponentPropertySave } from "../../class/smart-components/smart-componment-property-save";
import { ApplicationTheme } from "src/components/shared-components/enum/enumMisc";
import { AlertService } from "src/service/alert.service";
import { LoaderService } from "src/service/loader.service";
import { SmartComponentService } from "../../service/smart-component.service";
import { ThemeService } from "src/service/theme.service";
import { SkillFormat } from "../../enum/enumSkillFormat";
import { SmartComponentType } from "../../enum/enumSmartComponentType";
import { EnvironmentCategory } from "../../enum/enumEnvironment";
import { AssociationSmartComponent } from "../../interfaces/association-smart-component";
import { IconHelper } from "../../class/icon-helper";
import { PopupSearchSelectComponent } from "src/components/shared-components/components/general-components/popup-search-select/popup-search-select";
import { environment } from "src/environments/environment";
import { HideDataService } from "src/components/shared-components/service/hide-data.service";
import { MdSelectDirective } from "../../../../directive/md-select.directive";

@Component({
    selector: "app-associate-smart-component",
    templateUrl: "./associate-smart-component.component.html",
    styleUrls: ["./associate-smart-component.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonButton, IonCheckbox, IonIcon, IonInput, IonItem, IonLabel, IonSelect, IonSelectOption, MdSelectDirective, FormsModule, TranslatePipe]
})
export class AssociateSmartComponentComponent
  implements OnInit, OnDestroy {
  private readonly _translateService = inject(TranslateService);
  private readonly _themeService = inject(ThemeService);
  private readonly _alertService = inject(AlertService);
  private readonly _smartComponentService = inject(SmartComponentService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _modalCtrl = inject(ModalController);
  private readonly _hideDataService = inject(HideDataService);

  private _environmentDataSubscription: Subscription = null;
  private _hiddenModeSub: Subscription = null;
  private _idSmartComponent: number = 0;

  public isContentVisible: { [key: number]: boolean } = {};
  public selectedValue: any;
  public listEnvironmentAssociated: SmartComponentEnvironmentAssociated[] = [];
  public listProperties: SmartComponentProperty;
  public listPropertiesSave: SmartComponentPropertySave =
    new SmartComponentPropertySave();
  public listPropertiesNewSave: SmartComponentNewPropertySave =
    new SmartComponentNewPropertySave();
  /**
   * Environments shown in the list: the apartments and rooms of [listEnvironmentsOriginal] not yet associated, rooms
   * with the parent name in front, sorted by description; their masked copy while the hidden data mode is on
   */
  public listEnvironments: Environment[] = [];
  public selectedOption: number;
  public addedItems: string[] = [];

  public idSmartComponentSelected: number = 0;
  public skillFormat = SkillFormat;

  public listAssociated: {
    idSmartComponent: number;
    listEnvironment: Environment[];
  }[] = [];

  public showError: boolean = false;
  selectedComponents: number[] = [];
  selectedEnvironment: number | null = null;
  selectedEnvironments: { [key: string]: any } = {}; // Ambiente selezionato
  public isDataHidden: boolean = false;

  @Input() listSmartComponentKind: SmartComponentKind[] = [];
  @Input() listSmartComponent: SmartComponent[] = [];
  @Input() listEnvironmentsOriginal: Environment[] = [];
  @Input() idArea: number = 0;
  readonly reloadSmartComponent = output<any>();

  async ngOnInit() {
    this._hiddenModeSub = this._hideDataService.isDataHidden.subscribe(value => {
      this.isDataHidden = value;
    })
  }

  ngOnChanges() {
    if (
      this.listSmartComponent &&
      this.listSmartComponentKind &&
      this.listEnvironmentsOriginal
    ) {
      const elaboratedList = this.getElaboratedListEnvironment();
      this.sortByDescription(elaboratedList);
      this.listEnvironments = elaboratedList;

      this._hideDataService.envOriginalData.next(elaboratedList);
      this._environmentDataSubscription?.unsubscribe();
      this._environmentDataSubscription = this.showCorrectEnvironmentsList();

      this.listAssociated = this.listSmartComponent.map((comp) => {
        return {
          idSmartComponent: comp.id,
          listEnvironment: null,
        };
      });
    }
  }

  ngOnDestroy() {
    this._environmentDataSubscription?.unsubscribe();
    this._hiddenModeSub?.unsubscribe();
  }

  /**
   * Select/deselect all components
   * @param event
   */
  public toggleAll(event: any) {
    if (event.detail.checked) {
      this.selectedComponents = this.listSmartComponent.map((comp) => comp.id);
    } else {
      this.selectedComponents = [];
    }
  }

  /**
   * Select/deselect single component
   * @param id
   * @param event
   */
  public toggleSelection(id: number, event: any) {
    if (event.detail.checked) {
      if (!this.selectedComponents.includes(id)) {
        this.selectedComponents.push(id);
      }
    } else {
      this.selectedComponents = this.selectedComponents.filter(
        (compId) => compId !== id
      );
    }
  }

  /**
   * Get environments not associated with the provided smart component
   * @param idSmartComponent
   * @returns
   */
  public getEnvironmentsNotAssociated(idSmartComponent) {
    let associatedEnvironments;
    const associated = this.listAssociated.find(
      (item) => item.idSmartComponent === idSmartComponent
    );
    if (associated?.listEnvironment) {
      associatedEnvironments = associated
        ? associated?.listEnvironment.map((env) => env.id)
        : [];
      return this.listEnvironments.filter(
        (env) => !associatedEnvironments.includes(env.id)
      );
    } else {
      return this.listEnvironments;
    }
  }

  /**
   * Associate selected environments to the selected smart components
   * @returns
   */
  public associateEnvironment() {
    if (!this.selectedEnvironment) {
      alert(this._translateService.instant("dictionary.noEnvironmentSelected"));
      return;
    }

    if (this.selectedComponents.length === 0) {
      alert(this._translateService.instant("dictionary.noSmartcomponentSelected"));
      return;
    }

    this.listSmartComponent.forEach((component) => {
      if (this.selectedComponents.includes(component.id)) {
        this.listAssociated.forEach((assoc) => {
          if (assoc.idSmartComponent === component.id) {
            if (!assoc.listEnvironment) {
              assoc.listEnvironment = [];
            }

            const environmentToAdd = this.listEnvironments.find(
              (env) => env.id === this.selectedEnvironment
            );

            if (
              environmentToAdd &&
              !assoc.listEnvironment.some(
                (env) => env.id === environmentToAdd.id
              )
            ) {
              assoc.listEnvironment.push(environmentToAdd);
            }
          }
        });
      }
    });
  }

  /**
   * Get associated environments for the provided smart component
   */
  public getAssociatedEnvironments(idSmartComponent: number): Environment[] {
    const associated = this.listAssociated.find(
      (item) => item.idSmartComponent === idSmartComponent
    );
    return associated ? associated.listEnvironment : [];
  }

  /**
   * Returns the smart component type icon based on the provided ID
   * @param idSmartComponentKind
   * @returns {string}
   */
  public getTypeIcon(idSmartComponentKind: number): string {
    return IconHelper.getSmartComponentIconByType(idSmartComponentKind);
  }

  /**
   * Associate environment to smart component
   * @param idSmartComponent
   */
  public associateEnvironmentToComponent(idSmartComponent: number) {
    let valSel = this.selectedEnvironments[idSmartComponent];

    this.listAssociated.forEach((item) => {
      if (item.idSmartComponent === idSmartComponent) {
        const environmentToAdd = this.listEnvironments.find(
          (env) => env.id === valSel.envId
        );

        if (
          environmentToAdd &&
          (item.listEnvironment == null ||
            !item?.listEnvironment.some(
              (env) => env.id === environmentToAdd.id
            ))
        ) {
          item.listEnvironment = item.listEnvironment || [];
          item.listEnvironment.push(environmentToAdd);
        }
      }
    });
    this.selectedEnvironments[idSmartComponent] = {};
  }

  /**
   * Dissociate environment to smart component
   * @param idSmartComponent
   * @param idEnvironment
   */
  public dissociateEnvironmentToComponent(idSmartComponent, idEnvironment) {
    this.listAssociated.forEach((item) => {
      if (item.idSmartComponent === idSmartComponent) {
        item.listEnvironment = item.listEnvironment.filter(
          (env) => env.id !== idEnvironment
        );
      }
    });
  }

  /**
   * Elaborate list of environments to be shown in the list
   * Add parent description to the child environments
   * @returns
   */
  private getElaboratedListEnvironment(): any[] {
    let listEnvironmentTemp = this.listEnvironmentsOriginal.filter((item) => {
      // Show only environments of type apartment and room
      if (item.category === EnvironmentCategory.APARTAMENT || item.category === EnvironmentCategory.ROOM) {
        const found = this.listEnvironmentAssociated.find(
          (associated) => associated.idEnvironment === item.id
        );
        return !found;
      };
    });

    const noParentItems = listEnvironmentTemp.filter(
      (item) => !(item.idParent > 0)
    );
    const withParentItems = listEnvironmentTemp.filter(
      (item) => item.idParent > 0
    );

    const elaboratedList = [];

    noParentItems.forEach((item) => {
      elaboratedList.push(item);

      let children = withParentItems.filter(
        (child) => child.idParent === item.id
      );

      children = this.addParentDescriptionToChildren(children, item);

      elaboratedList.push(...children);
    });

    // Process remaining child environments that still need to be added
    let remainingItems = withParentItems.filter(
      (item) => !elaboratedList.some((sortedItem) => sortedItem.id === item.id)
    );
    remainingItems = this.addParentDescriptionToRemainingItems(remainingItems);

    elaboratedList.push(...remainingItems);

    return elaboratedList;
  }

  /**
   * Add parent description to children environments
   * @param children
   * @param parent
   * @returns
   */
  private addParentDescriptionToChildren(
    children: Environment[],
    parent: Environment
  ): any[] {
    return children.map((child) => {
      if (child.category == EnvironmentCategory.ROOM) {
        return {
          ...child,
          descriptionEnvironment: `${parent.descriptionEnvironment} - ${child.descriptionEnvironment}`,
        };
      } else {
        return child;
      }
    });
  }

  /**
   * Add parent description to remaining child environments
   * @param remainingItems
   * @returns
   */
  private addParentDescriptionToRemainingItems(remainingItems: any[]): any[] {
    return remainingItems.map((item) => {
      const parent = this.listEnvironmentsOriginal.find(
        (parentItem) => parentItem.id === item.idParent
      );
      if (item.category == EnvironmentCategory.ROOM) {
        return {
          ...item,
          descriptionEnvironment: `${parent?.descriptionEnvironment || ""} - ${item.descriptionEnvironment
            }`,
        };
      } else {
        return item;
      }
    });
  }

  /**
   * Sort the environments based on the descriptionEnvironment alphabetically
   * @param sortedList
   */
  private sortByDescription(sortedList: any[]): void {
    sortedList.sort((a, b) => {
      if (a.descriptionEnvironment < b.descriptionEnvironment) {
        return -1;
      }
      if (a.descriptionEnvironment > b.descriptionEnvironment) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Handle click on environment input
   * @param smartComponentId 
   */
  public async onClickOpenModalSelectEnvironment(smartComponentId: number): Promise<void> {
    let selectedItem: { name: string, value: number };

    if (this.selectedEnvironments[smartComponentId]) {
      selectedItem = { name: this.selectedEnvironments?.[smartComponentId].envName, value: this.selectedEnvironments?.[smartComponentId].envId }
    }

    const modal = await this._modalCtrl.create({
      component: PopupSearchSelectComponent,
      componentProps: {
        searchBarPlaceholder: this._translateService.instant("dictionary.searchEnvironment"),
        selectedItem: selectedItem,
        items: this.getEnvironmentsNotAssociated(smartComponentId).map(item => {
          return {
            name: item.descriptionEnvironment,
            value: item.id
          }
        }),
      }
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.selectedEnvironments[smartComponentId] = {
        envId: data.selectedValue,
        envName: data.selectedName
      }
    }
  }

  /**
   * Update environments list according to hide data mode
   */
  private showCorrectEnvironmentsList(): Subscription {
    return this._hideDataService.isDataHidden.subscribe(value => {

      if (value) {
        this.listEnvironments = this._hideDataService.hideEnvironmentsElaboratedData();
      } else {
        this.listEnvironments = this._hideDataService.envOriginalData.value;
      }
    })
  }
}
